/**
 * Inventory Movements API Service
 *
 * Data-access layer for the Move Inventory module.
 * Backed by tms-api-inventory (GET/POST /inventoryManagement/api/v1/inventory/movements/*)
 */
import { isAxiosError, type AxiosResponse } from 'axios';
import type {
  InventoryMovementSummary,
  MovementSearchResult,
  InventoryMovementDetail,
  MovementSearchType,
  BulkMoveValidationError,
} from '@tms-phoenix/tms-types';
import { BulkMoveValidationErrorSchema } from '@tms-phoenix/tms-types';
import { config, API_ROUTES } from '@/core/config';
import { getApiClient } from '@/core/services/api.service';
import { useEntitlementsStore } from '@/stores/entitlements.store';
import type { MovementRow, MovementSearchItem, LocationOption, MovementDetail } from '../types';

const api = getApiClient();
const PREFIX = `${API_ROUTES.INVENTORY_MANAGEMENT}/inventory/movements`;
/** Return the configured hospital ID or throw if it is missing. */
function requireHospitalId(): string {
  const id = config.hospitalId;
  if (!id) {
    throw new Error(
      '[inventory-movements.api] VITE_HOSPITAL_ID is not configured. Cannot proceed without a hospital context.',
    );
  }
  return id;
}

// ── Mappers ────────────────────────────────────────────────────────────────

function toMovementRow(m: InventoryMovementSummary): MovementRow {
  return {
    movementId: m.movement_id,
    containerIdentifier: m.container_identifier,
    fromLocationName: m.from_location_name,
    toLocationName: m.to_location_name,
    movedBy: m.moved_by,
    movedByName: m.moved_by_name,
    movedAt: m.moved_at,
    reason: m.reason,
    newExpirationDate: m.new_expiration_date,
  };
}

// containerType is the authoritative mother/donor/formulary signal -- entityType only tells
// you which search path matched (receivable/mother_donor_mrn both hit the same table).
const ENTITLEMENT_BY_CONTAINER_TYPE: Record<string, string> = {
  mother_bottle: 'inv.mother_milk_receive',
  donor_bottle: 'inv.donor_milk_receive',
  batch: 'inv.donor_milk_receive',
  product: 'inv.formulary',
};

/**
 * Move Inventory has no backend plan/entitlement gating (unlike receive/formulary
 * endpoints) -- P1/P2 tenants without Mother/Formulary access must still be prevented
 * from moving those item types, so the frontend filters search results itself.
 */
function isEntitledSearchItem(item: MovementSearchItem): boolean {
  const requiredEntitlement = item.entityType === 'lot_unit_id'
    ? 'inv.formulary'
    : ENTITLEMENT_BY_CONTAINER_TYPE[item.containerType ?? ''];
  return !requiredEntitlement || useEntitlementsStore().hasEntitlement(requiredEntitlement);
}

function toSearchItem(r: MovementSearchResult): MovementSearchItem {
  return {
    entityType: r.entity_type,
    entityId: r.entity_id,
    label: r.label,
    containerIdentifier: r.container_identifier,
    containerType: r.container_type,
    storageLocationId: r.storage_location_id,
    storageLocationName: r.storage_location_name,
    expiryAt: r.expiry_at,
    status: r.status,
    volumeMl: r.volume_ml,
    personName: r.person_name,
    pumpDate: r.pump_date,
    milkStatus: r.milk_status,
  };
}

// ── API functions ──────────────────────────────────────────────────────────

/**
 * Fetch inventory movements, optionally filtered by date range.
 * Server-paginated via `limit`/`offset` — the Recent tab passes a from_date
 * filter while Full History pages through the complete log.
 */
export async function listInventoryMovements(params?: {
  from_date?: string;
  to_date?: string;
  location?: string;
  container_identifier?: string;
  limit?: number;
  offset?: number;
}): Promise<MovementRow[]> {
  const res: AxiosResponse<InventoryMovementSummary[]> = await api.get(`${PREFIX}/all`, {
    params,
  });
  return (res.data ?? []).map(toMovementRow);
}

/**
 * Fetch a single container's full movement history — used to detect a past thaw
 * event (see wasThawedInHistory in move-inventory-expiry.ts) since there is no
 * persisted "has been thawed" flag on the milk record itself. The backend's
 * `container_identifier` filter is a case-insensitive substring match, so results
 * are narrowed to an exact match here before returning (whitespace-insensitive,
 * so leading/trailing whitespace on either side doesn't drop a legitimate match —
 * but case IS significant, e.g. `hm-1` does not match a stored `HM-1`).
 */
export async function fetchMovementHistoryForContainer(containerIdentifier: string): Promise<MovementRow[]> {
  const rows = await listInventoryMovements({ container_identifier: containerIdentifier });
  const normalize = (value: string | null | undefined) => value?.trim() ?? '';
  const target = normalize(containerIdentifier);
  return rows.filter((r) => normalize(r.containerIdentifier) === target);
}

/**
 * Search for moveable items across all entity types.
 *
 * `search_type` is required by MovementSearchQuerySchema whenever `q` is provided —
 * it tells the API exactly which search path to use (patient MRN, batch/bottle ID,
 * receivable, etc.), matching the "Search By" selector in MoveInventoryDialog.
 * `location` is sent as an additional filter param.
 *
 * `q`/`searchType` are optional so a location-only search can be issued (e.g. when
 * the location filter changes but the barcode/search field is empty) — the backend
 * schema only requires that at least one of `q` or `location` be present.
 *
 * `searchType` is required whenever `q` is provided — Axios silently drops
 * `undefined` params, so a missing `searchType` would otherwise result in a
 * `q`-only request the backend rejects with a 400. That failure mode is caught
 * here eagerly with a clear error instead.
 */
export async function searchMovementItems(
  q: string | undefined,
  searchType: MovementSearchType | undefined,
  location?: string,
): Promise<MovementSearchItem[]> {
  if (q && !searchType) {
    throw new Error('[inventory-movements.api] searchType is required when q is provided.');
  }
  const res: AxiosResponse<MovementSearchResult[]> = await api.get(`${PREFIX}/search`, {
    params: {
      ...(q ? { q, search_type: searchType } : {}),
      ...(location ? { location } : {}),
    },
  });
  return (res.data ?? []).map(toSearchItem).filter(isEntitledSearchItem);
}

/**
 * Atomically move one or more containers to a destination location.
 */
export async function bulkMoveInventory(payload: {
  to_location_id: string;
  reason: string;
  moved_by: string;
  items: Array<{
    entity_type: string;
    entity_id: string;
    current_location_id: string | null;
    new_expiry_date?: string;
    new_milk_status?: string;
  }>;
}): Promise<{ movement_ids: string[]; moved_count: number }> {
  const hospital_id = requireHospitalId();
  const res = await api.post(`${PREFIX}/bulk-move`, { hospital_id, ...payload });
  return res.data;
}

/**
 * Extracts the per-item `validation_errors` array from a bulk-move 400 VALIDATION_ERROR
 * response body (`{ error: { code: 'VALIDATION_ERROR', details: { validation_errors } } }`
 * — see BulkMoveValidationErrorSchema/inventoryMovementsService.bulkMove()). Returns []
 * for any other error shape (network error, 500, a different error code, etc.) so callers
 * can fall back to a generic toast message. Each entry is validated against
 * BulkMoveValidationErrorSchema — entries that don't match (unexpected shape from a
 * future API change) are dropped rather than trusted as-is.
 */
export function extractBulkMoveValidationErrors(err: unknown): BulkMoveValidationError[] {
  if (!isAxiosError(err)) return [];
  const apiError = err.response?.data?.error;
  if (apiError?.code !== 'VALIDATION_ERROR') return [];
  const errors = apiError.details?.validation_errors;
  if (!Array.isArray(errors)) return [];
  return errors
    .map((e) => BulkMoveValidationErrorSchema.safeParse(e))
    .filter((r): r is { success: true; data: BulkMoveValidationError } => r.success)
    .map((r) => r.data);
}

/**
 * Fetch full detail of a single inventory movement.
 */
export async function getInventoryMovement(movementId: string): Promise<MovementDetail> {
  const res: AxiosResponse<InventoryMovementDetail> = await api.get(`${PREFIX}/${movementId}`);
  const m = res.data;
  return {
    movementId: m.movement_id,
    containerIdentifier: m.container_identifier,
    containerType: m.container_type,
    fromLocationName: m.from_location_name,
    toLocationName: m.to_location_name,
    movedBy: m.moved_by,
    movedByName: m.moved_by_name,
    reason: m.reason,
    oldExpirationDate: m.old_expiration_date,
    newExpirationDate: m.new_expiration_date,
    status: m.status,
    createdAt: m.created_at,
  };
}

/**
 * Fetch storage locations (milk / feed entities: COLLECTION, MILK_BOTTLE, MILK_BATCH, FEED).
 * Matches the ReceiveMilkDialog endpoint — value is storage_location_id.
 */
export async function fetchStorageLocations(): Promise<LocationOption[]> {
  const res = await api.get(`${API_ROUTES.SETTINGS}/storage-locations/`);
  const data: Array<{ storage_location_id: string; name: string; is_active?: boolean; storage_type?: string | null }> =
    Array.isArray(res.data) ? res.data : [];
  return data
    .filter((l) => l.is_active !== false)
    .map((l) => ({ label: l.name, value: l.storage_location_id, storageType: l.storage_type ?? null }));
}

/**
 * Fetch inventory locations (formulary / PRODUCT_UNIT entities).
 * Matches the UpdateStockPage endpoint — value is location_id.
 *
 * Backend gates GET /settings/api/v1/inventory-locations/ behind the
 * 'inv.formulary' feature (plans without Formulary/Product, e.g. P1, don't
 * have it) — skip the call entirely rather than letting it 403.
 */
export async function fetchFormularyLocations(): Promise<LocationOption[]> {
  if (!useEntitlementsStore().hasEntitlement('inv.formulary')) return [];
  const res = await api.get(`${API_ROUTES.SETTINGS}/inventory-locations/`);
  const data: Array<{ location_id: string; name: string; is_active?: boolean; location_type?: string | null }> =
    Array.isArray(res.data) ? res.data : [];
  return data
    .filter((l) => l.is_active !== false)
    .map((l) => ({ label: l.name, value: l.location_id, storageType: l.location_type ?? null }));
}

/** Both spellings for the formulary/product entity type (API uses 'lot_unit_id'; legacy callers use 'PRODUCT_UNIT'). */
const FORMULARY_TYPES = new Set(['lot_unit_id', 'PRODUCT_UNIT']);

/**
 * Resolve the correct location list based on the entity types of selected items.
 * - All formulary (lot_unit_id / PRODUCT_UNIT) → inventory-locations
 * - Any milk/feed types → storage-locations
 * - Mixed → both combined (de-duped by value)
 */
export async function fetchLocationsByEntityTypes(entityTypes: string[]): Promise<LocationOption[]> {
  const hasFormulary = entityTypes.some((t) => FORMULARY_TYPES.has(t));
  const hasMilkOrFeed = entityTypes.some((t) => !FORMULARY_TYPES.has(t));

  if (hasFormulary && hasMilkOrFeed) {
    const [storage, formulary] = await Promise.all([fetchStorageLocations(), fetchFormularyLocations()]);
    const seen = new Set<string>();
    return [...storage, ...formulary].filter((l) => {
      if (seen.has(l.value)) return false;
      seen.add(l.value);
      return true;
    });
  }
  if (hasFormulary) return fetchFormularyLocations();
  return fetchStorageLocations();
}

/** @deprecated use fetchLocationsByEntityTypes */
export async function fetchMovementLocations(): Promise<LocationOption[]> {
  return fetchStorageLocations();
}

/**
 * Fetch every location type (storage + formulary) combined, regardless of entity type.
 * Used to populate the step-1 location filter, where the entity type of the items
 * to be searched is not yet known. Backed by two separate API calls.
 */
export async function fetchAllLocations(): Promise<LocationOption[]> {
  const [storage, formulary] = await Promise.all([fetchStorageLocations(), fetchFormularyLocations()]);
  const seen = new Set<string>();
  return [...storage, ...formulary].filter((l) => {
    if (seen.has(l.value)) return false;
    seen.add(l.value);
    return true;
  });
}
